import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { NomenclatureContext } from "src/shared/lib/hooks/context/getNomenclatureContext";
import { Nomenclature } from "src/enitities/Table/";
import { saveRow, removeRow, addRow } from "src/shared";
import { API, apiRequests } from "src/shared/api/api";
import { useLocation, useSearchParams } from "react-router-dom";
import { Space, message, Button } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { AddNomenclatureButton } from "src/widgets/Button";
import { AddNomenclatureStepButton } from "src/widgets/Button";
import FilterNomenclature from "src/shared/ui/Tables/FilterNomenclature";

export default function TableNomenclature() {
  const [messageApi, contextHolder] = message.useMessage();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, websocket, initialData, nomenclatureDataCount } =
    useContext(NomenclatureContext);
  const { pathname } = useLocation();
  const [dataSource, setDataSource] = useState(initialData || []);
  const [total, setTotal] = useState(nomenclatureDataCount);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Преобразование параметров из searchParams в формат API
  const buildParams = useCallback(
    (page, pageSize, name, extraFilters = {}) => {
      const params = {
        token: token,
        offset: (page - 1) * pageSize,
        limit: pageSize,
      };

      if (name) params.name = name;

      // Сбор параметров из searchParams
      searchParams.forEach((value, key) => {
        if (
          key !== "token" &&
          key !== "limit" &&
          key !== "offset" &&
          key !== "page"
        ) {
          if (key === "tags") {
            params[key] = value;
          } else if (
            key === "category" ||
            key === "global_category_id" ||
            key === "chatting_percent"
          ) {
            params[key] = Number(value);
          } else if (key === "has_photos") {
            if (value === "true") params.has_photos = true;
            else if (value === "false") params.has_photos = false;
            if (params.has_photos === true) params.with_photos = true;
          } else if (key === "with_photos") {
            params.with_photos = value === "true" || value === true;
          } else {
            params[key] = value;
          }
        }
      });

      // Переопределяем/добавляем фильтры из extraFilters (например, при ручном вызове)
      Object.keys(extraFilters).forEach((key) => {
        if (
          extraFilters[key] !== undefined &&
          extraFilters[key] !== null &&
          extraFilters[key] !== ""
        ) {
          params[key] = extraFilters[key];
        }
      });

      return params;
    },
    [token, searchParams],
  );

  // Загрузка данных с текущими параметрами
  const loadDataFromParams = useCallback(async () => {
    const params = buildParams(1, 10, searchParams.get("name"));
    try {
      const res = await apiRequests.nomenclature.getItems(params);
      setDataSource(res.data.result);
      setTotal(res.data.count);
      setPage(1);
      setPageSize(10);
    } catch (err) {
      console.error("Ошибка загрузки данных:", err);
    }
  }, [buildParams, searchParams]);

  // Загрузка данных с пагинацией
  const queryOffsetData = useCallback(
    (page, pageSize, name, filters = {}) => {
      const params = buildParams(page, pageSize, name, filters);
      setSearchParams({ token, ...params });
      apiRequests.nomenclature.getItems(params).then((res) => {
        setDataSource(res.data.result);
        setTotal(res.data.count);
        setPage(page);
        setPageSize(pageSize);
        return res.data;
      });
    },
    [buildParams, setSearchParams, token],
  );

  // Обновление данных при изменении параметров URL
  useEffect(() => {
    loadDataFromParams();
  }, [searchParams, loadDataFromParams]);

  // Обработчики изображений (оставлены без изменений)
  const handleSaveImage = useCallback(
    (picture) => {
      if (!picture || !picture.entity_id) return;
      const newData = JSON.parse(JSON.stringify(dataSource));
      const index = dataSource.findIndex(
        (item) => item.id === picture.entity_id,
      );
      if (index === -1 || !newData[index]) return;
      if (!newData[index].pictures) newData[index].pictures = [];
      const dubData = newData[index].pictures.filter(
        (item) => item.id === picture.id,
      );
      if (dubData.length === 0) {
        newData[index].pictures.push(picture);
      }
      setDataSource(newData);
    },
    [dataSource],
  );

  const handleDeleteImage = useCallback(
    async (id) => {
      // Логика удаления фото (можно оставить как есть)
      const newData = dataSource.map((item) => ({ ...item }));
      setDataSource(newData);
    },
    [dataSource],
  );

  // WebSocket обновления
  useEffect(() => {
    websocket.onmessage = (message) => {
      const data = JSON.parse(message.data);
      if (data.target === "nomenclature") {
        if (data.action === "create")
          addRow(dataSource, data.result, setDataSource);
        if (data.action === "edit")
          saveRow(dataSource, data.result, setDataSource);
        if (data.action === "delete")
          removeRow(dataSource, data.result.id, setDataSource);
      }
      if (data.target === "pictures") {
        if (data.action === "create") handleSaveImage(data.result);
        if (data.action === "delete") handleDeleteImage(data.result.id);
      }
    }; // eslint-disable-next-line
  }, [token, dataSource, handleSaveImage, handleDeleteImage]);

  return (
    <>
      {contextHolder}
      <Space direction="vertical" style={{ width: "100%", marginBottom: 10 }}>
        <Space style={{ alignItems: "center" }}>
          <AddNomenclatureButton />
          <AddNomenclatureStepButton />
          <Button
            type="text"
            icon={filtersOpen ? <DownOutlined /> : <RightOutlined />}
            onClick={() => setFiltersOpen((prev) => !prev)}
          >
            Фильтры поиска товаров
          </Button>
        </Space>
        <FilterNomenclature isOpen={filtersOpen} />
      </Space>
      {useMemo(
        () => (
          <Nomenclature
            page={page}
            dataSource={dataSource}
            handleSave={API.crud.edit(token, pathname, messageApi)}
            handleRemove={API.crud.remove(token, pathname, messageApi)}
            handleSaveImage={handleSaveImage}
            handleDeleteImage={API.pictures.removeImage(token, messageApi)}
            queryOffsetData={queryOffsetData}
            total={total}
            pageSize={pageSize}
            name={searchParams.get("name")}
            messageApi={messageApi}
          />
        ),
        [
          dataSource,
          page,
          token,
          pathname,
          total,
          pageSize,
          handleSaveImage,
          queryOffsetData,
          messageApi,
          searchParams,
        ],
      )}
    </>
  );
}
